import React, { useEffect, useState } from "react";
import {
  FaBriefcase,
  FaCheck,
  FaEdit,
  FaStickyNote,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import {
  displayProfileValue,
} from "./tenantProfileFields";

function DetailItem({ label, value, children }) {
  return (
    <div className="cportal-profile-view-item">
      <span className="cportal-profile-view-label">{label}</span>
      <span className="cportal-profile-view-value">{children ?? displayProfileValue(value)}</span>
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <h3 className="cportal-detail-section-title cportal-company-section-title">
      {Icon ? <Icon className="cportal-detail-section-icon" aria-hidden="true" /> : null}
      {children}
    </h3>
  );
}

function initials(profile) {
  const first = (profile?.First_Name || "").trim().charAt(0);
  const last = (profile?.Last_Name || "").trim().charAt(0);
  return `${first}${last}`.toUpperCase() || "U";
}

function fullName(profile) {
  const name = (profile?.Full_Name || "").trim();
  if (name) return name;
  return `${(profile?.First_Name || "").trim()} ${(profile?.Last_Name || "").trim()}`.trim() || "User";
}

function phoneDisplay(profile) {
  const cc = profile?.Country_Code ? String(profile.Country_Code).trim() : "";
  const phone = profile?.Phone || profile?.Phone_Number || "";
  const digits = phone ? String(phone).trim() : "";
  if (!digits) return "—";
  return cc ? `${cc} ${digits}` : digits;
}

function phoneTelHref(profile) {
  const cc = profile?.Country_Code ? String(profile.Country_Code).trim() : "";
  const phone = profile?.Phone || profile?.Phone_Number || "";
  const digits = phone ? String(phone).replace(/[^\d]/g, "") : "";
  if (!digits) return null;
  const codeDigits = cc.replace(/[^\d]/g, "");
  return `tel:${codeDigits ? `+${codeDigits}` : ""}${digits}`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return displayProfileValue(value);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function avatarStatusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-avatar-status is-active";
  if (s === "inactive" || s === "terminated" || s === "resigned") {
    return "cportal-avatar-status is-danger";
  }
  return "cportal-avatar-status";
}

function TenantProfileView() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/client/me");
        if (!active) return;
        setProfile(res.data?.data || {});
      } catch (_) {
        if (active) setError("Could not load your profile.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return <p className="cportal-muted">Loading…</p>;
  }

  if (error) {
    return <div className="cportal-alert cportal-alert-error">{error}</div>;
  }

  const loginEmail = profile?.Company_Email || profile?.Email;
  const hasProfessionalInfo = [
    profile?.Employee_Id,
    profile?.Designation,
    profile?.Department,
    profile?.Gender,
    profile?.DOJ,
  ].some(Boolean);
  const phone = phoneDisplay(profile);
  const phoneHref = phoneTelHref(profile);
  const altPhone = profile?.Alt_Phone ? String(profile.Alt_Phone).trim() : "";

  return (
    <div className="cportal-card cportal-company-profile-card cportal-my-profile-card">
      <div className="cportal-card-head">
        <div className="cportal-card-head-title">
          <div className="cportal-entity-avatar-wrap">
            <div className="cportal-my-profile-avatar" aria-hidden="true">
              {initials(profile)}
            </div>
            {profile?.Status ? (
              <span
                className={avatarStatusClass(profile.Status)}
                role="img"
                aria-label={`Status: ${displayProfileValue(profile.Status)}`}
                title={displayProfileValue(profile.Status)}
              >
                {String(profile.Status).toLowerCase() === "active" ? <FaCheck aria-hidden="true" /> : null}
              </span>
            ) : null}
          </div>
          <div className="cportal-my-profile-head-meta">
            <h2>{fullName(profile)}</h2>
          </div>
        </div>
        <button
          type="button"
          className="cportal-btn cportal-btn-edit"
          onClick={() => navigate("/portal/profile/edit")}
        >
          <FaEdit aria-hidden="true" /> <span className="cportal-btn-text">Edit</span>
        </button>
      </div>

      <div className="cportal-profile-view-grid cportal-my-profile-fields">
        <DetailItem label="First name" value={profile.First_Name} />
        <DetailItem label="Last name" value={profile.Last_Name} />
        <DetailItem label="Company Email (login)" value={loginEmail}>
          {loginEmail ? (
            <a href={`mailto:${loginEmail}`} className="cportal-contact-link">{loginEmail}</a>
          ) : (
            "—"
          )}
        </DetailItem>
        <DetailItem label="Personal Email" value={profile.Personal_Email}>
          {profile.Personal_Email ? (
            <a href={`mailto:${profile.Personal_Email}`} className="cportal-contact-link">
              {profile.Personal_Email}
            </a>
          ) : (
            "—"
          )}
        </DetailItem>
        <DetailItem label="Phone" value={phone}>
          {phoneHref ? (
            <a href={phoneHref} className="cportal-contact-link">{phone}</a>
          ) : (
            phone
          )}
        </DetailItem>
        <DetailItem label="Alt phone" value={altPhone}>
          {altPhone ? (
            <a href={`tel:${altPhone.replace(/[^\d+]/g, "")}`} className="cportal-contact-link">
              {altPhone}
            </a>
          ) : (
            "—"
          )}
        </DetailItem>
      </div>

      {hasProfessionalInfo ? (
        <>
          <SectionTitle icon={FaBriefcase}>Professional information</SectionTitle>
          <div className="cportal-profile-view-grid">
            <DetailItem label="Employee ID" value={profile.Employee_Id} />
            <DetailItem label="Designation" value={profile.Designation} />
            <DetailItem label="Department" value={profile.Department} />
            <DetailItem label="Gender" value={profile.Gender} />
            <DetailItem label="Date of joining">{formatDate(profile.DOJ)}</DetailItem>
            <DetailItem label="Company" value={profile.Company_Name} />
          </div>
        </>
      ) : null}

      {profile.Notes ? (
        <>
          <SectionTitle icon={FaStickyNote}>Notes</SectionTitle>
          <p className="cportal-notes-text">{String(profile.Notes || "").trim() || "—"}</p>
        </>
      ) : null}
    </div>
  );
}

export default TenantProfileView;
